import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import type { PermissionValue } from '@/features/auth/types';
import { WorkspaceId, WorkspaceConfig, getAccessibleWorkspaces } from '../workspace-config';
import { cn } from '@/utils/cn';
import { useMotionConfig } from '@/hooks/useMotionConfig';
import { Button, Icon, IconButton } from '../../../components/common';

export interface WorkspaceSelectorProps {
    isOpen: boolean;
    currentWorkspace: WorkspaceId | null;
    userPermissions: PermissionValue[];
    onSelect: (workspaceId: WorkspaceId) => void;
    onClose: () => void;
}

export const WorkspaceSelector: React.FC<WorkspaceSelectorProps> = ({
    isOpen,
    currentWorkspace,
    userPermissions,
    onSelect,
    onClose,
}) => {
    const motionConfig = useMotionConfig();
    const accessibleWorkspaces = getAccessibleWorkspaces(userPermissions);

    const handleSelect = (workspaceId: WorkspaceId) => {
        onSelect(workspaceId);
        onClose();
    };

    return (
        <AnimatePresence>
            {isOpen ? (
                <motion.div
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                    onClick={onClose}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={motionConfig.subtle}
                >
                    <motion.div
                        className="bg-white dark:bg-surface-dark rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                        initial={{ scale: 0.95, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.95, opacity: 0 }}
                        transition={motionConfig.overlay}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-border-dark">
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Select Workspace
                            </h2>
                            <IconButton
                                onClick={onClose}
                                className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
                                icon="close"
                                label="Close"
                                variant="ghost"
                            />
                        </div>

                        {/* Workspace Grid */}
                        <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]">
                            {accessibleWorkspaces.length === 0 ? (
                                <div className="text-center py-12">
                                    <Icon name="lock" size="xl" className="text-gray-300 dark:text-gray-600 mb-4" />
                                    <p className="text-gray-600 dark:text-gray-400">
                                        You do not have access to any tools. Contact your Stage Manager for access.
                                    </p>
                                </div>
                            ) : (
                                <div className="@container">
                                <div className="grid grid-cols-1 @sm:grid-cols-2 gap-4">
                                    {accessibleWorkspaces.map((workspace) => (
                                        <WorkspaceCard
                                            key={workspace.id}
                                            workspace={workspace}
                                            isActive={currentWorkspace === workspace.id}
                                            onClick={() => handleSelect(workspace.id)}
                                        />
                                    ))}
                                </div>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="px-6 py-4 border-t border-gray-200 dark:border-border-dark bg-gray-50 dark:bg-white/5">
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                <span className="font-semibold">Tip:</span> Press{' '}
                                <kbd className="px-1.5 py-0.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded text-xs">
                                    Cmd/Ctrl + Space
                                </kbd>{' '}
                                to open this selector
                            </p>
                        </div>
                    </motion.div>
                </motion.div>
            ) : null}
        </AnimatePresence>
    );
};

/**
 * Individual workspace card
 */
interface WorkspaceCardProps {
    workspace: WorkspaceConfig;
    isActive: boolean;
    onClick: () => void;
}

const WorkspaceCard: React.FC<WorkspaceCardProps> = ({ workspace, isActive, onClick }) => (
        <Button
            onClick={onClick}
            size="sm"
            variant="ghost"
            className={cn(
                'flex min-h-0 items-start justify-start gap-4 rounded-lg border-2 p-4 text-left shadow-none transition-all',
                isActive
                    ? 'border-primary bg-primary/5 dark:bg-primary/10'
                    : 'border-gray-200 dark:border-border-dark hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-white/5'
            )}
        >
            <div className={cn(
                'flex items-center justify-center w-12 h-12 rounded-lg',
                isActive
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-400'
            )}>
                <Icon name={workspace.icon} size="lg" />
            </div>
            <div className="flex-1 min-w-0">
                <h3 className={cn(
                    'font-semibold text-sm mb-1',
                    isActive
                        ? 'text-primary'
                        : 'text-gray-900 dark:text-white'
                )}>
                    {workspace.label}
                    {isActive ? (
                        <span className="ml-2 text-xs font-normal opacity-75">
                            (Active)
                        </span>
                    ) : null}
                </h3>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                    {workspace.description}
                </p>
            </div>
        </Button>
    );

WorkspaceSelector.displayName = 'WorkspaceSelector';
